import { cn } from '@commenda/utils';
import React from 'react';

const CARD =
  'flex w-full flex-col overflow-hidden rounded-xl border-1/2 border-border-secondary bg-bg-primary shadow-[0px_1px_3px_0px_rgba(18,18,18,0.06),0px_1px_2px_0px_rgba(18,18,18,0.1)]';

export interface SettingGroupEmptyState {
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export interface SettingGroupProps {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  dividers?: boolean;
  empty?: SettingGroupEmptyState;
  children?: React.ReactNode;
  className?: string;
}

export const SettingDivider = () => (
  <div className="flex h-[0.5px] w-full shrink-0 px-lg">
    <div className="h-[0.5px] w-full bg-border-secondary" />
  </div>
);

export const SettingGroup = ({
  title,
  description,
  action,
  dividers = true,
  empty,
  children,
  className,
}: SettingGroupProps) => {
  const rows = React.Children.toArray(children).filter(Boolean);
  const showEmpty = rows.length === 0 && empty !== undefined;

  return (
    <div className={cn('flex w-full flex-col gap-2xl', className)}>
      {title ? (
        <div className="flex w-full items-start gap-lg px-lg">
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="txt-medium-medium text-text-primary">{title}</span>
            {description ? (
              <span className="txt-small-regular text-text-tertiary">
                {description}
              </span>
            ) : null}
          </div>
          {action}
        </div>
      ) : null}

      <div className={CARD}>
        {showEmpty ? (
          <SettingGroupEmpty {...empty} />
        ) : (
          rows.map((row, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              {dividers && index > 0 ? <SettingDivider /> : null}
              {row}
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};

const SettingGroupEmpty = ({
  title,
  description,
  action,
}: SettingGroupEmptyState) =>
  description ? (
    <div className="flex min-h-16 w-full items-center gap-lg p-lg">
      <div className="flex min-w-0 flex-1 flex-col gap-xxs">
        <span className="txt-small-medium text-text-primary">{title}</span>
        <span className="txt-extra-small-regular text-text-secondary">
          {description}
        </span>
      </div>
      {action}
    </div>
  ) : (
    <div className="flex min-h-16 w-full items-center gap-lg pl-lg pr-3xl">
      <span className="txt-small-regular min-w-0 flex-1 text-text-tertiary">
        {title}
      </span>
      {action}
    </div>
  );
